/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import {
  getPostC6StatusByTxnIds,
  getPublishStatusByBulkRequestId
} from '../services/bulkPublishService';
import {
  bulkActionFieldNames,
  PUBLISH_STATUS
} from '../constants/bulkActionStatusConstants';
import {
  toPublishProjectListTitleCase,
  determinePostC6StatusFromRequest
} from '../utils/bulkActionUtils';

// Helper function to determine status from a bulk request
function determineStatusFromRequest(request) {
  // Convert status to Title Case
  const rawStatus = request?.status || PUBLISH_STATUS.QUEUED;
  return toPublishProjectListTitleCase(rawStatus);
}

function getCompletedCountFromRequest(statusCounts) {
  if (!statusCounts) return 0;
  const completedCount =
    statusCounts?.find(
      s => s?.status.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    )?.count || 0;
  const falilureCount =
    statusCounts?.find(
      s =>
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase() ||
        s?.status.toLowerCase() ===
          PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase() ||
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILED.toLowerCase()
    )?.count || 0;
  return parseInt(completedCount, 10) + parseInt(falilureCount, 10);
}

function getProgressFromRequest(statusCounts, totalCount) {
  const completedCount =
    statusCounts?.find(
      s => s?.status.toLowerCase() === PUBLISH_STATUS.DELIVERED.toLowerCase()
    )?.count || 0;
  const falilureCount =
    statusCounts?.find(
      s =>
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILURE.toLowerCase() ||
        s?.status.toLowerCase() ===
          PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase() ||
        s?.status.toLowerCase() === PUBLISH_STATUS.FAILED.toLowerCase()
    )?.count || 0;
  const total = completedCount + falilureCount;
  return (total / totalCount) * 100;
}

// ...existing code...

const ProjectType = types.model('BulkPublishProject', {
  id: types.optional(types.identifier, () =>
    Math.random().toString(36).substring(2, 9)
  ),
  txnId: types.maybeNull(types.string),
  operationType: types.optional(types.string, 'Publish'),
  title: types.maybeNull(types.string),
  userid: types.maybeNull(types.string),
  publishInitiateTime: types.maybeNull(types.string),
  startTime: types.maybeNull(types.string),
  endTime: types.maybeNull(types.string),
  errorDetails: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  postC6Status: types.maybeNull(types.string),
  bulkRequestId: types.maybeNull(types.string)
});

// Define the main status tracking model
export default types
  .model('BulkPublishProjectList', {
    publishProjectList: types.optional(types.array(ProjectType), []),
    hasMore: types.maybeNull(types.boolean, false),
    totalCount: types.maybeNull(types.number, 0),
    sortBy: types.optional(types.string, bulkActionFieldNames.START_TIME),
    sortDirection: types.optional(types.enumeration(['asc', 'desc']), 'desc'),
    page: types.optional(types.number, 1),
    pageSize: types.optional(types.number, 100),
    isLoading: types.optional(types.boolean, false),
    total: types.optional(types.number, 10),
    completed: types.optional(types.number, 10),
    progress: types.optional(types.number, 0),
    statusCounts: types.optional(
      types.array(
        types.model({
          status: types.string,
          count: types.number
        })
      ),
      []
    )
  })
  .views(self => ({
    get sortedPublishProjectData() {
      const sortableData = [...self.publishProjectList];

      if (self.sortBy) {
        sortableData.sort((a, b) => {
          if (a[self.sortBy] < b[self.sortBy]) {
            return self.sortDirection === 'asc' ? -1 : 1;
          }
          if (a[self.sortBy] > b[self.sortBy]) {
            return self.sortDirection === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      return sortableData;
    }
  }))
  .actions(self => ({
    resetBulkPageIndex() {
      self.page = 1;
    },

    resetList() {
      self.bulkPublishReqIds = [];
      self.hasMore = false;
      self.totalCount = 0;
      self.total = 0;
    },
    setBulkProjectList(bulkRequests) {
      self.publishProjectList = bulkRequests;
    },
    setHasMore(hasMore) {
      self.hasMore = hasMore;
    },
    setTotalCount(totalCount) {
      self.totalCount = totalCount;
    },
    setNewPageValue(page) {
      self.page = page;
    },
    setStatusCounts(statusCounts) {
      self.statusCounts = statusCounts;
    },
    setSorting(field) {
      if (self.sortBy === field) {
        // Toggle direction if same field
        self.sortDirection = self.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to ascending
        self.sortBy = field;
        self.sortDirection = 'asc';
      }
    },
    setIsLoading(isLoading) {
      self.isLoading = isLoading;
    },
    setCompletedCount(statusCounts) {
      // Sets the sum of completed and error counts as "completed" for progress tracking
      self.completed = getCompletedCountFromRequest(statusCounts);
    },
    setProgress(progress) {
      self.progress = progress;
    },
    async getPostC6Status(transactionIds) {
      const postc6Stauses = await getPostC6StatusByTxnIds({ transactionIds });
      if (!postc6Stauses || Object.keys(postc6Stauses).length === 0) return;
      // Update only the relevant items in publishProjectList immutably
      const updatedList = self.publishProjectList.map(item => {
        if (item && postc6Stauses[item.txnId]) {
          return {
            ...toJS(item),
            postC6Status: determinePostC6StatusFromRequest(
              postc6Stauses[item.txnId]
            )
          };
        }
        return item;
      });
      self.setBulkProjectList(updatedList);
    },

    async loadBulkPublishProjects(bulkRequestId, resetProjects) {
      const apiParams = {
        bulkRequestId,
        page: 1,
        pageSize: self.pageSize
      };

      if (resetProjects) {
        self.resetBulkPageIndex();
      }
      self.setNewPageValue(1);
      self.setIsLoading(true);
      const {
        records,
        hasMore = false,
        totalCount = 0,
        statusCounts = []
      } = await getPublishStatusByBulkRequestId(apiParams);
      const newItems = [];

      await records.forEach(request => {
        // Define default values for fields
        const status = determineStatusFromRequest(request);
        const postC6Status = determinePostC6StatusFromRequest(
          request?.postC6Status
        );
        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          txnId: request.txnId || '',
          operationType: 'Publish',
          title: request.title || '',
          userid: request.userid || '',
          publishInitiateTime: request.publishInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: request.failureDetails || '',
          status,
          postC6Status,
          bulkRequestId: request.bulkRequestId
        });
      });
      self.setBulkProjectList(newItems);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
      self.setStatusCounts(statusCounts);
      self.setIsLoading(false);
      const progress = getProgressFromRequest(statusCounts, totalCount);
      self.setProgress(progress);
      self.setCompletedCount(statusCounts);
    },

    async updateBulkPublishProjectList(bulkRequestId) {
      const updatedPageValue = self.page + 1;
      self.setNewPageValue(updatedPageValue);
      const apiParams = {
        bulkRequestId,
        page: updatedPageValue || self.page,
        pageSize: self.pageSize
      };
      self.setIsLoading(true);
      const {
        records,
        hasMore = false,
        totalCount = 0,
        statusCounts = []
      } = await getPublishStatusByBulkRequestId(apiParams);
      const newItems = [];
      records.forEach(request => {
        const status = determineStatusFromRequest(request);
        const postC6Status = determinePostC6StatusFromRequest(
          request?.postC6Status
        );
        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          txnId: request.txnId || '',
          operationType: 'Publish',
          title: request.title || '',
          userid: request.userid || '',
          publishInitiateTime: request.publishInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: request.failureDetails || '',
          status,
          postC6Status,
          total: request.count || 0,
          bulkRequestId: request.bulkRequestId
        });
      });
      const currentBulkProjectList = [
        ...toJS(self.publishProjectList),
        ...newItems
      ];

      self.setBulkProjectList(currentBulkProjectList);
      self.setHasMore(hasMore);
      self.setTotalCount(totalCount);
      self.setStatusCounts(statusCounts);
      self.setIsLoading(false);
    }
  }));
